import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Card,
    Typography,
    message,
    Modal,
    Descriptions,
    Image,
    Input,
    Tabs,
} from 'antd';
import {
    DollarOutlined,
    CheckOutlined,
    CloseOutlined,
    EyeOutlined,
    PictureOutlined,
} from '@ant-design/icons';

import api, { personalExpenseAPI } from '../../services/api';
import dayjs from 'dayjs';
import { getReceiptUrl } from '../../utils/urlConfig';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FinanceApprovalPage = () => {
    const [loading, setLoading] = useState(false);
    const [pendingExpenses, setPendingExpenses] = useState([]);
    const [completedExpenses, setCompletedExpenses] = useState([]);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        fetchExpenses();
    }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            // ⭐ 재무 승인 대기 (finance_pending 상태)
            const pendingResponse = await api.get('/personal-expenses', {
                params: {
                    for_finance_approval: 'true',  // ⭐ 추가
                    status: 'finance_pending'
                }
            });
            setPendingExpenses(pendingResponse.data.expenses || []);

            // ⭐ 입금 완료 (paid 상태)
            const completedResponse = await api.get('/personal-expenses', {
                params: {
                    for_finance_approval: 'true',  // ⭐ 추가
                    status: 'paid'
                }
            });
            setCompletedExpenses(completedResponse.data.expenses || []);
        } catch (error) {
            console.error('Fetch expenses error:', error);
            message.error(error.response?.data?.message || '경비 목록 조회 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (expense) => {
        setSelectedExpense(expense);
        setDetailModalVisible(true);
    };

    const handleApproveClick = (expense) => {
        setSelectedExpense(expense);
        setConfirmModalVisible(true);
    };

    const handleApprove = async () => {
        try {
            await personalExpenseAPI.financeProcess(selectedExpense.id);
            message.success('입금이 완료되었습니다');
            setConfirmModalVisible(false);
            setSelectedExpense(null);
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '입금 처리 실패');
        }
    };

    const handleRejectClick = (expense) => {
        setSelectedExpense(expense);
        setRejectModalVisible(true);
        setRejectReason('');
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            message.warning('반려 사유를 입력해주세요');
            return;
        }

        try {
            await personalExpenseAPI.reject(selectedExpense.id, {
                reject_reason: rejectReason
            });
            message.success('경비가 반려되었습니다');
            setRejectModalVisible(false);
            setSelectedExpense(null);
            setRejectReason('');
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '반려 처리 실패');
        }
    };

    const categoryMap = {
        meal: '식비',
        transport: '교통비',
        accommodation: '숙박비',
        supplies: '소모품',
        communication: '통신비',
        entertainment: '접대비',
        other: '기타',
    };

    const columns = [
        {
            title: 'PM 승인일',
            dataIndex: 'pm_approved_at',
            key: 'pm_approved_at',
            width: 100,
            render: (date) => dayjs(date).format('MM-DD'),
        },
        {
            title: '신청자',
            dataIndex: ['user', 'name'],
            key: 'user',
            width: 100,
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.user?.employee_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            width: 200,  // ⭐ 너비 지정
            ellipsis: true,
        },
        {
            title: '지출일',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: 100,
            render: (date) => dayjs(date).format('MM-DD'),
        },
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (category) => (
                <Tag color="blue">{categoryMap[category] || category}</Tag>
            ),
        },
        {
            title: '지출처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 120,
            ellipsis: true,
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text strong>{parseInt(amount).toLocaleString()}원</Text>
            ),
        },
        {
            title: '영수증',
            dataIndex: 'receipt_url',
            key: 'receipt_url',
            width: 80,
            align: 'center',
            render: (url) =>
                url ? (
                    <Image
                        width={40}
                        src={getReceiptUrl(url)}
                        placeholder={<PictureOutlined />}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                    />
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
        {
            title: '작업',
            key: 'action',
            width: 300,  // ⭐ 버튼 3개를 위한 충분한 너비
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">  {/* ⭐ size="small"로 변경 */}
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        상세
                    </Button>
                    {activeTab === 'pending' && (
                        <>
                            <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={() => handleApproveClick(record)}
                                style={{ fontSize: 12 }}  // ⭐ 폰트 크기 축소
                            >
                                입금완료
                            </Button>
                            <Button
                                danger
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() => handleRejectClick(record)}
                            >
                                반려
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const completedColumns = [
        {
            title: '입금일',
            dataIndex: 'finance_processed_at',
            key: 'finance_processed_at',
            width: 100,
            render: (date) => dayjs(date).format('MM-DD'),
        },
        ...columns.slice(1, -1),
        {
            title: '작업',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetail(record)}
                >
                    상세
                </Button>
            ),
        },
    ];

    const tabItems = [
        {
            key: 'pending',
            label: (
                <span>
                    입금 대기
                    {pendingExpenses.length > 0 && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                            {pendingExpenses.length}
                        </Tag>
                    )}
                </span>
            ),
            children: (
                <Table
                    columns={columns}
                    dataSource={pendingExpenses}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}  // ⭐ 가로 스크롤 추가
                    locale={{ emptyText: '입금 대기 중인 경비가 없습니다' }}
                    summary={(data) => {
                        const totalAmount = data.reduce(
                            (sum, item) => sum + parseFloat(item.amount),
                            0
                        );
                        return totalAmount > 0 ? (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={6}>
                                        <Text strong>합계</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                            {totalAmount.toLocaleString()}원
                                        </Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        ) : null;
                    }}
                />
            ),
        },
        {
            key: 'completed',
            label: '입금 완료',
            children: (
                <Table
                    columns={completedColumns}
                    dataSource={completedExpenses}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: '입금 완료된 경비가 없습니다' }}
                />
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>경비 입금 처리</Title>
            </div>

            <Card style={{ marginTop: 24 }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            </Card>

            {/* 상세 모달 */}
            <Modal
                title="경비 상세"
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedExpense(null);
                }}
                footer={null}
                width={700}
            >
                {selectedExpense && (
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="신청자" span={2}>
                            {selectedExpense.user?.name} ({selectedExpense.user?.employee_id})
                        </Descriptions.Item>
                        <Descriptions.Item label="프로젝트" span={2}>
                            {selectedExpense.project?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="지출일">
                            {dayjs(selectedExpense.expense_date).format('YYYY-MM-DD')}
                        </Descriptions.Item>
                        <Descriptions.Item label="구분">
                            <Tag color="blue">
                                {categoryMap[selectedExpense.category] || selectedExpense.category}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="지출처" span={2}>
                            {selectedExpense.vendor}
                        </Descriptions.Item>
                        <Descriptions.Item label="금액" span={2}>
                            <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                {parseInt(selectedExpense.amount).toLocaleString()}원
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="사용 목적" span={2}>
                            {selectedExpense.purpose}
                        </Descriptions.Item>
                        <Descriptions.Item label="은행">
                            {selectedExpense.user?.bank_name || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="계좌번호">
                            {selectedExpense.user?.bank_account ? (
                                <Text copyable>{selectedExpense.user.bank_account}</Text>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="예금주" span={2}>
                            {selectedExpense.user?.account_holder || '-'}
                        </Descriptions.Item>
                        {selectedExpense.receipt_url && (
                            <Descriptions.Item label="영수증" span={2}>
                                <Image
                                    src={getReceiptUrl(selectedExpense.receipt_url)}
                                    alt="영수증"
                                    width={200}
                                    preview={{
                                        mask: (
                                            <div>
                                                <EyeOutlined style={{ marginRight: 4 }} />
                                                크게 보기
                                            </div>
                                        )
                                    }}
                                />
                            </Descriptions.Item>
                        )}
                        {selectedExpense.pm_approved_at && (
                            <Descriptions.Item label="PM 승인일" span={2}>
                                {dayjs(selectedExpense.pm_approved_at).format('YYYY-MM-DD HH:mm')}
                            </Descriptions.Item>
                        )}
                        {selectedExpense.paid_at && (
                            <Descriptions.Item label="입금일" span={2}>
                                {dayjs(selectedExpense.paid_at).format('YYYY-MM-DD HH:mm')}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* 입금 확인 모달 */}
            <Modal
                title="입금 완료 확인"
                open={confirmModalVisible}
                onOk={handleApprove}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    setSelectedExpense(null);
                }}
                okText="입금 완료"
                cancelText="취소"
            >
                {selectedExpense && (
                    <div>
                        <Text strong>다음 계좌로 입금을 완료하셨습니까?</Text>
                        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
                            <Descriptions.Item label="신청자">
                                {selectedExpense.user?.name}
                            </Descriptions.Item>
                            <Descriptions.Item label="은행">
                                {selectedExpense.user?.bank_name || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="계좌번호">
                                {selectedExpense.user?.bank_account ? (
                                    <Text copyable>{selectedExpense.user.bank_account}</Text>
                                ) : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="예금주">
                                {selectedExpense.user?.account_holder || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="입금 금액">
                                <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                    {parseInt(selectedExpense.amount).toLocaleString()}원
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>

            {/* 반려 모달 */}
            <Modal
                title="경비 반려"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setSelectedExpense(null);
                    setRejectReason('');
                }}
                okText="반려"
                okButtonProps={{ danger: true }}
                cancelText="취소"
            >
                {selectedExpense && (
                    <div>
                        <Text>
                            {selectedExpense.user?.name}님의 경비 {parseInt(selectedExpense.amount).toLocaleString()}원을 반려하시겠습니까?
                        </Text>
                        <div style={{ marginTop: 16 }}>
                            <Text strong>반려 사유 *</Text>
                            <TextArea
                                rows={4}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="반려 사유를 입력해주세요"
                                style={{ marginTop: 8 }}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FinanceApprovalPage;